import React, { useCallback, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";

import TerritoryLevel from "@mapmycustomers/shared/enum/TerritoryLevel";
import LongLat from "@mapmycustomers/shared/types/base/LongLat";
import { EntityType, Territory } from "@mapmycustomers/shared/types/entity";
import SmartTerritoryConfig from "@mapmycustomers/shared/types/territory/SmartTerritoryConfig";
import PlatformFilterModel from "@mapmycustomers/shared/types/viewModel/platformModel/PlatformFilterModel";
import { parseAreaType } from "@mapmycustomers/shared/util/territory/areaTypes";

import DataView from "@app/component/DataView";
import DataViewEntityTypeCount from "@app/component/DataView/type/DataViewEntityTypeCount";
import useMap from "@app/component/map/FeaturedMap/utils/useMap";
import useMapView from "@app/component/view/utils/useMapView";
import { ensureDemographicDataLoaded } from "@app/scene/map/store/layers/boundaries/actions";
import { getDemographicData } from "@app/scene/map/store/layers/boundaries/selectors";
import { RegionDemographicData } from "@app/scene/map/store/layers/boundaries/types/BoundariesLayerData";
import { toggleDataViewVisibility } from "@app/scene/map/store/territoryMode/actions";
import {
  getTerritoryDataViewData,
  getTerritoryDataViewEntityTypeCount,
  getTerritoryId,
  getTerritoryMode,
} from "@app/scene/map/store/territoryMode/selectors";
import { RootState } from "@app/store/rootReducer";
import loggingService from "@app/util/logging";
import convertMapFilterToPlatformFilterModel from "@app/util/viewModel/convertMapFilterToPlatformFilterModel";

import { TERRITORY_BANNER_CLASS_NAME } from "../../utils/consts";
import useAggregateDemographyData from "../../utils/useAggregateDemographyData";

import styles from "./DataViewWrapper.module.scss";

interface Props {
  data?: LongLat[][] | SmartTerritoryConfig;
  demographicData?: RegionDemographicData;
  entityTypeCount: DataViewEntityTypeCount;
  isViewMode: boolean;
  onEnsureDemographicDataLoaded: typeof ensureDemographicDataLoaded.request;
  onToggleDataViewVisibility: typeof toggleDataViewVisibility;
  territoryId?: Territory["id"];
}

const DataViewWrapper: React.FC<Props> = ({
  data,
  demographicData,
  entityTypeCount,
  isViewMode,
  onEnsureDemographicDataLoaded,
  onToggleDataViewVisibility,
  territoryId,
}) => {
  const { map } = useMap();
  const [style, setStyle] = useState<React.CSSProperties>({});
  const {
    viewState: { filter, viewAs },
  } = useMapView();

  useEffect(() => {
    onEnsureDemographicDataLoaded();
  }, [onEnsureDemographicDataLoaded]);

  useEffect(() => {
    const infoBlockQueryResult = document.getElementsByClassName(TERRITORY_BANNER_CLASS_NAME);

    try {
      const observer = new ResizeObserver(() => {
        const height = (infoBlockQueryResult[0] as HTMLElement)?.offsetHeight ?? 0;
        setStyle({ height: `calc(100% - ${height}px)`, top: height });
      });
      if (infoBlockQueryResult.length) {
        observer.observe(infoBlockQueryResult[0]);
      }
      return () => {
        observer.disconnect();
      };
    } catch (e) {
      loggingService.error("Error in DataViewWrapper", e);
    }
  }, [setStyle]);

  const getDrawerContainer = useCallback(() => {
    const selection = map?.getDiv().parentElement?.querySelectorAll(".mmc-map-content");

    if (selection?.length) {
      return selection[0] as HTMLElement;
    }
    return document.body as HTMLElement;
  }, [map]);

  const multipolygon = useMemo(() => {
    if (Array.isArray(data)) {
      if (data.length > 0) {
        if (data[0].length > 0) {
          return [[...data[0], data[0][0]]];
        }
      }
    }
    return undefined;
  }, [data]);

  const regionDemographyData = useAggregateDemographyData(
    Array.isArray(data) ? undefined : data?.areaType,
    Array.isArray(data) ? undefined : data?.regionIds,
    demographicData
  );

  const dataViewFilter: PlatformFilterModel = useMemo(() => {
    let result = convertMapFilterToPlatformFilterModel(
      filter,
      [EntityType.COMPANY, EntityType.PERSON, EntityType.DEAL],
      viewAs
    );
    if (data && !Array.isArray(data) && !isViewMode) {
      const smartTerritoryConfig: SmartTerritoryConfig = data;
      const parsedAreaType = parseAreaType(smartTerritoryConfig.areaType);
      if (!parsedAreaType) {
        throw new Error(`Can't parse smart territory config area type`);
      }
      const { countryCode, level } = parsedAreaType;
      const fieldToFilterBy =
        level === TerritoryLevel.ADM1
          ? "geoAddress.regionCode"
          : level === TerritoryLevel.ADM2
          ? "fipsCode"
          : "geoAddress.postalCode";

      result = {
        ...result,
        $and: [
          ...(result.$and ?? []),
          { "geoAddress.countryCode": countryCode },
          { [fieldToFilterBy]: { $in: smartTerritoryConfig.regionIds } },
        ],
      };
      const { territories, ...restFilter } = result;
      result = restFilter;
    } else if (isViewMode && territoryId) {
      result = { ...result, territories: { $any: [territoryId] } };
    }
    if (multipolygon) {
      result = { ...result, multipolygon };
    }
    return result;
  }, [data, filter, isViewMode, multipolygon, territoryId, viewAs]);

  return (
    <DataView
      className={styles.container}
      drawerStyle={style}
      entityTypeRecordsCount={entityTypeCount}
      filter={dataViewFilter}
      getDrawerContainer={getDrawerContainer}
      hideTotalCount
      onHide={onToggleDataViewVisibility}
      regionDemographyData={regionDemographyData}
      totalCount={0}
      useAnglesCloseButton
    />
  );
};

const mapStateToProps = (state: RootState) => ({
  data: getTerritoryDataViewData(state),
  demographicData: getDemographicData(state),
  entityTypeCount: getTerritoryDataViewEntityTypeCount(state),
  isViewMode: getTerritoryMode(state) === "view",
  territoryId: getTerritoryId(state),
});

const mapDispatchToProps = {
  onEnsureDemographicDataLoaded: ensureDemographicDataLoaded.request,
  onToggleDataViewVisibility: toggleDataViewVisibility,
};

export default connect(mapStateToProps, mapDispatchToProps)(DataViewWrapper);
